import { z } from 'zod';
import { paginationQuerySchema } from '../common/pagination.js';
import { createPdaPurchaseSchema, nationalityRefSchema, pdaContextSchema, pdaCustomerSchema, quantityWordsSchema, } from './pda.contract.js';
import { actorSchema, productSchema } from './primitives.js';
/**
 * The Fuel PDA offline mode (docs/rebuild/fuel-pda.md section 13): the signed
 * sale QR (`MF1`), the offline grant, and the synchronisation endpoints
 * `GET /pda/sync/pull` and `POST /pda/sync/push`, plus the dashboard list of
 * sync conflicts. Money never appears here.
 */
// --- temporary reference and device ----------------------------------------
/** The device code: 6 uppercase base32 characters, generated once per install (section 13.5). */
export const PDA_DEVICE_CODE_PATTERN = /^[A-Z2-7]{6}$/;
/** `PD-{deviceCode}-{seq}`: the reference an offline purchase prints until it is synced. */
export const PDA_TEMPORARY_REFERENCE_PATTERN = /^PD-[A-Z2-7]{6}-[1-9]\d{0,8}$/;
export const pdaTemporaryReferenceSchema = z
    .string()
    .trim()
    .transform((value) => value.toUpperCase())
    .pipe(z.string().regex(PDA_TEMPORARY_REFERENCE_PATTERN, 'not a temporary reference'));
/** Whether a scanned or typed text is a temporary reference (`PD-…`). */
export function isPdaTemporaryReference(value) {
    return PDA_TEMPORARY_REFERENCE_PATTERN.test(value.trim().toUpperCase());
}
/** The device identifier sent with every push: the device code, or a longer install id. */
export const pdaDeviceIdSchema = z.string().trim().min(1).max(64);
// --- signed sale QR (MF1, section 13.2) --------------------------------------
/** The kind of a deliverable sale in the QR: cash sale, debt sale, PDA sale. */
export const mf1SaleKindSchema = z.enum(['CASH', 'DEBT', 'PDA']);
/**
 * The decoded payload of `MF1.<payload>.<signature>`. Short keys keep the QR
 * small; absent optionals are omitted, never null.
 */
export const mf1PayloadSchema = z.object({
    /** Key id: which `qrKeys` entry verifies the signature. */
    i: z.string().min(1).max(32),
    /** The sale reference, e.g. `SA-2026-12363` or `DM-2026-501`. */
    r: z.string().min(1).max(40),
    /** Branch id. */
    b: z.string().min(1),
    /** Product code. */
    p: productSchema,
    k: mf1SaleKindSchema,
    /** Quantity lines `[categoryName (Arabic), count]`, counts > 0. */
    q: z.array(z.tuple([z.string(), z.number().int().positive()])),
    /** Deducted jerrycans («إلا جركل»), a decimal string, `0` when none. */
    d: z.string(),
    /** Net jerrycans `B x k + J - D`, a decimal string. */
    n: z.string(),
    /** Customer or debtor name. */
    c: z.string().optional(),
    /** Vehicle plate. */
    v: z.string().optional(),
    /** Issue time, epoch seconds. */
    t: z.number().int().nonnegative(),
});
/** One public key of the QR signature: raw 32-byte Ed25519 key, base64url without padding. */
export const pdaQrKeySchema = z.object({
    id: z.string().min(1).max(32),
    publicKey: z.string().regex(/^[A-Za-z0-9_-]{43}$/, 'a raw 32-byte key in base64url'),
});
// --- offline grant (section 13.4) -------------------------------------------
/**
 * What the device may do offline, signed by the server (HMAC). The plain
 * fields are for the device; `token` carries the same data with the
 * signature and is sent back with every pushed operation.
 */
export const pdaOfflineGrantSchema = z.object({
    token: z.string().min(1),
    userId: z.string(),
    nameAr: z.string(),
    nameEn: z.string(),
    branchId: z.string(),
    /** The device the grant was issued to (the pull's `deviceId`). */
    deviceId: z.string(),
    /** The user's services (permissions) at issue time. */
    services: z.array(z.string()),
    /** The products the user may work with at issue time. */
    products: z.array(productSchema),
    issuedAt: z.iso.datetime({ offset: true }),
    /** The last online sign-in + 24 hours. */
    expiresAt: z.iso.datetime({ offset: true }),
});
// --- GET /pda/sync/pull -----------------------------------------------------
export const pdaSyncPullQuerySchema = z.object({
    /** The device the offline grant is bound to: push refuses the grant from another device. */
    deviceId: pdaDeviceIdSchema,
    /** The `serverTime` of the previous pull; without it every list is full. */
    since: z.iso.datetime({ offset: true }).optional(),
});
/**
 * One deliverable sale of the branch (section 4.3 data, never a price):
 * completed, undelivered, not deleted, of a product the user may access.
 */
export const pdaPendingSaleSchema = z.object({
    reference: z.string(),
    kind: z.enum(['CASH_SALE', 'PDA_SALE', 'DEBT_SALE']),
    product: productSchema,
    lines: z.array(z.object({ categoryName: z.string(), qty: z.number().int() })),
    /** «إلا جركل», a decimal string (`0` when none). */
    lessJerrycans: z.string(),
    netQuantity: quantityWordsSchema,
    /** The debtor (debt sale) or purchase customer (PDA sale) name; null for a cash sale. */
    customerName: z.string().nullable(),
    vehiclePlate: z.string().nullable(),
    createdAt: z.iso.datetime({ offset: true }),
});
export const pdaSyncPullSchema = z.object({
    /** Send back as `since` on the next pull. */
    serverTime: z.iso.datetime({ offset: true }),
    context: pdaContextSchema,
    /** Empty while the signing key is not configured (signed QR off). */
    qrKeys: z.array(pdaQrKeySchema),
    offlineGrant: pdaOfflineGrantSchema,
    nationalities: z.array(nationalityRefSchema),
    /** The branch's purchase customers (D13). */
    customers: z.object({
        items: z.array(pdaCustomerSchema),
        removedIds: z.array(z.string()),
    }),
    pendingSales: z.object({
        items: z.array(pdaPendingSaleSchema),
        /** No longer deliverable since `since`: delivered, deleted, or moved branch. */
        removedReferences: z.array(z.string()),
    }),
});
// --- POST /pda/sync/push ----------------------------------------------------
/** Most operations in one push (section 13.7). */
export const PDA_SYNC_BATCH_MAX = 50;
export const pdaSyncOperationKindSchema = z.enum(['PURCHASE', 'DELIVERY']);
/** PURCHASE data: the online purchase body plus the temporary reference it printed. */
export const pdaSyncPurchaseDataSchema = createPdaPurchaseSchema.extend({
    temporaryReference: pdaTemporaryReferenceSchema,
    /**
     * إرسال واتساب was tapped offline: the server sends the purchase message
     * after acceptance, through the online path and its switch.
     */
    sendWhatsapp: z.boolean().optional(),
});
/** DELIVERY data: the delivered reference, the scanned QR when it was signed, and the device time. */
export const pdaSyncDeliveryDataSchema = z.object({
    reference: z
        .string()
        .trim()
        .min(1)
        .max(40)
        .transform((value) => value.toUpperCase()),
    qr: z.string().startsWith('MF1.').max(2000).optional(),
    deliveredAt: z.iso.datetime({ offset: true }),
});
/**
 * One queued operation. `data` is checked per operation by the server (with
 * the schemas above), so one malformed operation is REFUSED alone instead of
 * failing the whole batch.
 */
export const pdaSyncOperationSchema = z.object({
    /** UUID made on the device: the idempotency key of this operation. */
    id: z.uuid(),
    /** The device sequence number; operations are applied in this order. */
    seq: z.number().int().nonnegative(),
    kind: pdaSyncOperationKindSchema,
    /** The offline grant token the operation was saved under. */
    grant: z.string().min(1).max(4000),
    /** Device time of the save. */
    enteredAt: z.iso.datetime({ offset: true }),
    data: z.unknown(),
});
export const pdaSyncPushSchema = z.object({
    deviceId: pdaDeviceIdSchema,
    operations: z
        .array(pdaSyncOperationSchema)
        .min(1)
        .max(PDA_SYNC_BATCH_MAX)
        .refine((ops) => new Set(ops.map((op) => op.id)).size === ops.length, {
        message: 'each operation id may appear once',
    }),
});
/**
 * ACCEPTED: applied (or applied before). WAITING: frozen or no open shift, the
 * device retries. CONFLICT: not applied, recorded for the office. REFUSED:
 * never applied (invalid grant, missing permission, invalid data).
 */
export const pdaSyncStatusSchema = z.enum(['ACCEPTED', 'WAITING', 'CONFLICT', 'REFUSED']);
export const pdaSyncResultSchema = z.object({
    id: z.string(),
    status: pdaSyncStatusSchema,
    /** The final reference (`PU-…` of a purchase, the delivered sale's reference). */
    reference: z.string().optional(),
    /** A stable error code the device translates (e.g. `ALREADY_DELIVERED`, `SYSTEM_FROZEN`). */
    code: z.string().optional(),
    /** English detail for logs; never shown to the employee. */
    message: z.string().optional(),
    /** ALREADY_DELIVERED: who delivered it and when, like the online refusal. */
    details: z.record(z.string(), z.string()).optional(),
});
export const pdaSyncPushResultSchema = z.object({ results: z.array(pdaSyncResultSchema) });
// --- review flags and conflicts (section 13.8, dashboard) -------------------
export const pdaSyncConflictStatusSchema = z.enum(['OPEN', 'RESOLVED']);
export const pdaSyncConflictListQuerySchema = paginationQuerySchema.extend({
    status: pdaSyncConflictStatusSchema.default('OPEN'),
    kind: pdaSyncOperationKindSchema.optional(),
});
/** CONFLICT: not applied. REFUSED: never applied. REVIEW: applied and flagged (debt sale delivery). */
export const pdaSyncConflictOutcomeSchema = z.enum(['CONFLICT', 'REFUSED', 'REVIEW']);
export const pdaSyncConflictRowSchema = z.object({
    id: z.string(),
    outcome: pdaSyncConflictOutcomeSchema.default('CONFLICT'),
    deviceId: z.string(),
    kind: pdaSyncOperationKindSchema,
    operationId: z.string(),
    reference: z.string().nullable(),
    /** The refusal code, e.g. `ALREADY_DELIVERED`, or a review reason of a debt sale delivery. */
    code: z.string(),
    user: actorSchema,
    deviceTime: z.iso.datetime({ offset: true }),
    serverTime: z.iso.datetime({ offset: true }),
    details: z.record(z.string(), z.string()).nullable(),
    /** The operation data as sent (a refused purchase can be entered again by the office). */
    data: z.unknown().nullable().default(null),
    resolvedBy: actorSchema.nullable(),
    resolvedAt: z.iso.datetime({ offset: true }).nullable(),
});
//# sourceMappingURL=pda-sync.contract.js.map